import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { colors } from '../../styles/colors'
import MediaQuery from 'react-responsive'
import { formatedDate } from '../../utils/formatedDate'
import { Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import BootstrapTable from 'react-bootstrap-table-next'
import paginationFactory from 'react-bootstrap-table2-paginator'
import filterFactory from 'react-bootstrap-table2-filter'
import apiClient from '../../utils/api/apiClient'
import { APIEndpoints } from '../../utils/api/apiConfig'
import TableProvider, { useTableState } from '../Table/TableProvider'
import UniversalTableWrapperTest from '../Table/UniversalTableWrapperTest'
import { StyledCard } from '../Cards/StyledCard'
import { columns } from './filterConfig'

const StyledCardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 0;

  .pending-delivery-info {
    font-size: small;
    color: gray;
    font-weight: initial;
  }
`

const StyledComment = styled.div`
  color: ${colors.gray3};
  margin-top: 8px;
  padding: 4px;
  box-shadow: rgb(0 0 0 / 20%) 0px 0px 15px;
  font-style: italic;
  background: white;
`

const StyledHeaderCell = styled.div`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;

  .pending-delivery-info {
    font-size: small;
    color: gray;
    font-weight: initial;
  }
`

const getPendingDeliveryAmount = (item) => {
  const orderedQuantity = item.orderedQuantity || 0
  const shippedQuantity = item.shippedQuantity || 0
  return orderedQuantity - shippedQuantity
}

const FETCH_ALL_LIMIT = 1000

const getByPath = (obj, path) =>
  path
    .split('.')
    .reduce((value, key) => (value == null ? value : value[key]), obj)

const InventoryHistoryListInner = ({ businessPartnerId }) => {
  const { t } = useTranslation()
  const [inventoryHistory, setInventoryHistory] = useState([])
  const [tableIsLoading, setTableIsLoading] = useState(false)
  const [filter, setFilter] = useState({})
  const [mobileSearchTerm, setMobileSearchTerm] = useState('')
  const {
    tableRef,
    paginationOptions,
    setPageLimit,
    setPageOffset,
    setTotalCount,
    currentPage,
    totalPages,
  } = useTableState()

  const fetchHistoryData = useCallback(() => {
    setTableIsLoading(true)
    const params = { 'page.limit': FETCH_ALL_LIMIT, 'page.offset': 0 }
    apiClient
      .getJson(
        APIEndpoints.businessPartner(businessPartnerId).inventorHistory,
        params,
        true,
        true,
      )
      .then((response) => {
        setInventoryHistory(
          response.data.map((item, index) => ({ ...item, __rowKey: index })),
        )
      })
      .finally(() => setTableIsLoading(false))
  }, [businessPartnerId])

  useEffect(() => {
    if (businessPartnerId) {
      fetchHistoryData()
    }
  }, [businessPartnerId, fetchHistoryData])

  const filteredData = useMemo(() => {
    const activeFilters = Object.entries(filter).filter(([, value]) => value)
    if (!activeFilters.length) return inventoryHistory
    return inventoryHistory.filter((item) =>
      activeFilters.every(([dataField, value]) => {
        const cellValue = getByPath(item, dataField)
        return String(cellValue ?? '')
          .toLowerCase()
          .includes(value.toLowerCase())
      }),
    )
  }, [inventoryHistory, filter])

  useEffect(() => {
    setTotalCount(filteredData.length)
  }, [filteredData, setTotalCount])

  const mobileFilteredData = useMemo(() => {
    if (!mobileSearchTerm) return inventoryHistory
    const term = mobileSearchTerm.toLowerCase()
    const searchableFields = columns
      .filter((column) => column.filter && column.dataField !== 'comment')
      .map((column) => column.dataField)
    return inventoryHistory.filter((item) =>
      searchableFields.some((dataField) =>
        String(getByPath(item, dataField) ?? '')
          .toLowerCase()
          .includes(term),
      ),
    )
  }, [inventoryHistory, mobileSearchTerm])

  const handleTableChange = (type, newState) => {
    if (type === 'pagination' || type === 'filter') {
      setPageLimit(newState.sizePerPage)
      setPageOffset(
        newState.page === 1
          ? 0
          : newState.sizePerPage * newState.page - newState.sizePerPage,
      )
    }
    if (type === 'filter') {
      const { filters } = newState
      const newFilter = {}
      columns.forEach((column) => {
        if (column.filter) {
          newFilter[column.dataField] = filters[column.dataField]
            ? filters[column.dataField].filterVal
            : ''
        }
      })
      setFilter(newFilter)
    }
  }

  const preparedColumns = useMemo(() => {
    const tmpColumns = columns.map((column) => ({ ...column }))
    tmpColumns.forEach((tmpColumn) => {
      tmpColumn.headerFormatter = (column, colIndex, components) => (
        <StyledHeaderCell title={t(tmpColumn.text)}>
          {t(tmpColumn.text)}
          {components.sortElement}
          {tmpColumn.headerFormatterExtra && tmpColumn.headerFormatterExtra()}
          <div className='d-flex align-items-end'>
            {components.filterElement}
          </div>
        </StyledHeaderCell>
      )
      tmpColumn.text = t(tmpColumn.text)
    })
    return tmpColumns
  }, [t])

  return (
    <React.Fragment>
      <MediaQuery maxWidth={992}>
        <>
          <Form.Control
            type='text'
            value={mobileSearchTerm}
            onChange={(e) => setMobileSearchTerm(e.target.value)}
            placeholder={t('search')}
            className='mb-3'
          />
          <StyledCardList>
            {mobileFilteredData.map((item, index) => (
              <StyledCard key={`list-${index}`}>
                <StyledCard.Body>
                  <StyledCard.Body.Title>
                    {formatedDate(item.createdAt)}
                  </StyledCard.Body.Title>
                  <div>
                    {item.article.displayName} {item.article.articleNumber}
                  </div>
                  <div>
                    {t('size')}: {item.articleVariant.displayName}
                  </div>
                  <div>
                    {t('type')}:{' '}
                    <span className='text-uppercase'>{item.type}</span>
                  </div>
                  <div>
                    {t('variation')}: {item.quantity}
                    {item.type === 'ordered' ? (
                      <span className='shipping-sum'>
                        &nbsp;
                        {getPendingDeliveryAmount(item) === 0
                          ? null
                          : `(${getPendingDeliveryAmount(item)}*)`}
                      </span>
                    ) : null}
                  </div>
                  <div className='pending-delivery-info'>
                    * {t('inventory:pendingDelivery')}
                  </div>
                  {item.comment && (
                    <StyledComment>{item.comment}</StyledComment>
                  )}
                </StyledCard.Body>
              </StyledCard>
            ))}
          </StyledCardList>
        </>
      </MediaQuery>
      <MediaQuery minWidth={993}>
        <UniversalTableWrapperTest
          tableIsLoading={tableIsLoading}
          currentPage={currentPage}
          totalPages={totalPages}
        >
          <BootstrapTable
            ref={tableRef}
            loading={tableIsLoading}
            noDataIndication={() => (
              <div className='no-data-placeholder'>
                {t('administration:noResultsFound')}
              </div>
            )}
            onTableChange={handleTableChange}
            bootstrap4
            keyField='__rowKey'
            hover
            data={filteredData}
            columns={preparedColumns}
            pagination={paginationFactory(paginationOptions)}
            filter={filterFactory()}
            bordered={false}
          />
        </UniversalTableWrapperTest>
      </MediaQuery>
    </React.Fragment>
  )
}

const InventoryHistoryList = ({ businessPartnerId }) => {
  return (
    <TableProvider>
      <InventoryHistoryListInner businessPartnerId={businessPartnerId} />
    </TableProvider>
  )
}

export default InventoryHistoryList
